import React, { useCallback, useMemo } from 'react';
import * as PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import Typography from '../../mui/Components/Typography';
import { IdPropType } from '../../../proptypes/basic';
import { useCustomer } from '../../customers/customersSlice';
import { useCareNeed } from '../../careNeeds/careNeedsSlice';
import { useNurseRequestsByCareNeedId } from '../../nurseRequests/nurseRequestsSlice';
import { selectAllAgencies } from '../../agencies/agenciesSlice';
import { careNeedDashboardPath } from '../../nav/paths';
import {
    getCareNeedDashboardFilterWarnings,
    showCustomerOnCareNeedDashboard,
} from '../../kanban/careNeedDashboardFilters';

/**
 * Shown instead of the BK-Einsatz dialog when a BK-Bedarf has no agency yet
 * (docs/plans/care-need-single-agency.md, rule 4 / open question 1): explains why,
 * says where on the Einsatzboard the BK-Bedarf is right now, and opens the board
 * filtered to this customer.
 */
const CareNeedWithoutAgencyDialog = ({ open, onClose, customerId, careNeedId }) => {
    const history = useHistory();
    const [customer] = useCustomer(customerId);
    const [careNeed] = useCareNeed(careNeedId);
    const [nurseRequests] = useNurseRequestsByCareNeedId(careNeedId);
    const agencies = useSelector(selectAllAgencies);

    const customerName = customer && customer.displayNames ? customer.displayNames[0] : '';

    const openRequestAgencies = useMemo(
        () =>
            (nurseRequests || [])
                .filter((request) => !request.closedAt)
                .map((request) => agencies.find((agency) => agency.id === request.agencyId))
                .filter((agency, index, self) => agency && self.indexOf(agency) === index)
                .map((agency) => agency.matchCode || agency.name),
        [nurseRequests, agencies]
    );

    // read when the dialog renders - the filters live in localStorage, not in the store
    const filterWarnings = open ? getCareNeedDashboardFilterWarnings(customer, careNeed) : [];

    const handleOpenDashboard = useCallback(() => {
        if (customerName) showCustomerOnCareNeedDashboard(customerName);
        onClose();
        history.push(careNeedDashboardPath);
    }, [customerName, onClose, history]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Noch keine Agentur für diesen BK-Bedarf</DialogTitle>
            <DialogContent>
                <Box mb={2}>
                    <Typography>
                        Ein BK-Bedarf gehört genau einer Agentur. Die Agentur steht fest, sobald ein
                        BK-Vorschlag bestätigt ist – dabei wird auch der erste BK-Einsatz
                        automatisch angelegt. Ohne Agentur gibt es keine Betreuungskraft und damit
                        noch keinen BK-Einsatz.
                    </Typography>
                </Box>
                <Box mb={2}>
                    <Alert severity="info">
                        {openRequestAgencies.length > 0
                            ? `Es laufen BK-Anfragen an ${openRequestAgencies.length} ${
                                  openRequestAgencies.length === 1 ? 'Agentur' : 'Agenturen'
                              } (${openRequestAgencies.join(
                                  ', '
                              )}). Sobald ein Vorschlag vorgestellt wurde, steht der Kunde im Einsatzboard in der Spalte „Vorgestellt“ – dort den Vorschlag bestätigen.`
                            : 'Für diesen BK-Bedarf wurden noch keine BK-Anfragen verschickt. Der Kunde steht im Einsatzboard in der Spalte „BK-Bedarf“ – dort die BK-Anfragen versenden.'}
                    </Alert>
                </Box>
                {filterWarnings.length > 0 && (
                    <Box mb={2}>
                        <Alert severity="warning">
                            Ihre Filter im Einsatzboard blenden diesen Kunden aus:
                            <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                                {filterWarnings.map((warning) => (
                                    <li key={warning}>{warning}</li>
                                ))}
                            </ul>
                            Bitte den Filter dort anpassen.
                        </Alert>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Schließen</Button>
                <Button variant="contained" color="primary" onClick={handleOpenDashboard}>
                    {customerName ? `Zum Einsatzboard (${customerName})` : 'Zum Einsatzboard'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

CareNeedWithoutAgencyDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    customerId: IdPropType.isRequired,
    careNeedId: IdPropType,
};

CareNeedWithoutAgencyDialog.defaultProps = {
    careNeedId: null,
};

export default CareNeedWithoutAgencyDialog;
